import React, { useEffect, useState } from "react";
import { ReactComponent as BaseVector } from "../../../../icons/baricon.svg";
import { ReactComponent as SimulationVector } from "../../../../icons/simulation_vector.svg";
import { ReactComponent as OptimalVector } from "../../../../icons/optimal_vector.svg";
import { ReactComponent as Info } from "../../../../icons/fluent_info.svg";

interface CostItem {
  label: string;
  value: string;
  percentage?: string;
  isPositive?: boolean;
  hasPercentage?: boolean;
}

interface MetricValue {
  value: number | null;
  pct_change: number | null;
}

interface BaseData {
  max_deficit: MetricValue;
  net_surplus_deficit: MetricValue;
  dam_purchase: MetricValue;
  dam_sell: MetricValue;
  offers_purchase: MetricValue;
  avg_surplus: MetricValue;
  no_of_deficit_slot: MetricValue;
  no_of_surplus_slot: MetricValue;
  dam_purchase_rs: MetricValue;
  dam_sell_rs: MetricValue;
  offers_purchase_rs: MetricValue;
  average_buy_rate: MetricValue;
  average_sell_rate: MetricValue;
  net_cost: MetricValue;
}

interface CostSection {
  base: BaseData;
  simulation: BaseData;
  optimal: BaseData;
}

interface DisplaySection {
  title: string;
  titleColor: string;
  iconColor: string;
  iconBgColor: string;
  items: CostItem[];
}

const SkeletonLoader = () => {
  return (
    <div className="flex gap-6 w-full">
      {[1, 2, 3].map((section) => (
        <div
          key={section}
          className="bg-white border border-[#DFEAF2] rounded-2xl p-2 w-full h-full animate-pulse"
        >
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 p-2 mb-2 bg-white border-b border-[#DFEAF2]"
              >
                <div className="bg-gray-200 rounded-full w-9 h-9 flex-shrink-0"></div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="h-4 bg-gray-200 rounded w-40"></div>
                    <div className="w-5 h-5 bg-gray-200 rounded"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function GnaStatisticsCostAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading = false,
  data,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading?: boolean;
  data: CostSection[];
}) {
  const [costData, setCostData] = useState<DisplaySection[]>([]);

  const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return "-";
    // Use Math.abs and manually add sign to avoid locale issues
    const absNum = Math.abs(num);
    const formatted = absNum.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
    return formatted;
  };
  const formatPercentage = (
    pct: number | null,
  ): { text: string; isPositive: boolean; hasValue: boolean } => {
    if (pct === null) return { text: "", isPositive: true, hasValue: false };
    const isPositive = pct >= 0;
    const arrow = isPositive ? "↑" : "↓";
    const absValue = Math.abs(pct);

    // Format percentage: if it has decimals, show 2 decimal places, otherwise show as integer
    const formattedPct =
      absValue % 1 === 0 ? absValue.toFixed(0) : absValue.toFixed(2);

    return {
      text: `${arrow} ${formattedPct}%`,
      isPositive,
      hasValue: true,
    };
  };

  useEffect(() => {
    if (data && data.length > 0) {
      const section = data[0];

      const createItems = (sectionData: BaseData, isBase: boolean = false) => {
        const damPurchasePct = formatPercentage(
          sectionData.dam_purchase_rs.pct_change,
        );
        const damSellPct = formatPercentage(sectionData.dam_sell_rs.pct_change);
        const offersPct = formatPercentage(
          sectionData.offers_purchase_rs.pct_change,
        );
        const avgBuyRatePct = formatPercentage(
          sectionData.average_buy_rate.pct_change,
        );
        const avgSellRatePct = formatPercentage(
          sectionData.average_sell_rate.pct_change,
        );

        return [
          {
            label: "DAM Purchase (Lakh ₹)",
            value: formatNumber(sectionData.dam_purchase_rs.value),
            percentage: damPurchasePct.text,
            isPositive: damPurchasePct.isPositive,
            hasPercentage: isBase ? false : damPurchasePct.hasValue,
          },
          {
            label: "DAM Sell (Lakh ₹)",
            value: formatNumber(sectionData.dam_sell_rs.value),
            percentage: damSellPct.text,
            isPositive: damSellPct.isPositive,
            hasPercentage: isBase ? false : damSellPct.hasValue,
          },
          {
            label: "Offers Purchased (Lakh ₹)",
            value: formatNumber(sectionData.offers_purchase_rs.value),
            percentage: offersPct.text,
            isPositive: offersPct.isPositive,
            hasPercentage: isBase ? false : offersPct.hasValue,
          },
          {
            label: "Average Purchase Rate (₹/KWh)",
            value: formatNumber(sectionData.average_buy_rate.value),
            percentage: avgBuyRatePct.text,
            isPositive: avgBuyRatePct.isPositive,
            hasPercentage: isBase ? false : avgBuyRatePct.hasValue,
          },
          {
            label: "Average Sell Rate (₹/KWh)",
            value: formatNumber(sectionData.average_sell_rate.value),
            percentage: avgSellRatePct.text,
            isPositive: avgSellRatePct.isPositive,
            hasPercentage: isBase ? false : avgSellRatePct.hasValue,
          },
        ];
      };

      const formattedData: DisplaySection[] = [
        {
          title: "Base",
          titleColor: "text-[#819ACC]",
          iconColor: "text-[#819ACC]",
          iconBgColor: "bg-blue-100",
          items: createItems(section.base, true),
        },
        {
          title: "Simulated",
          titleColor: "text-[#F1935C]",
          iconColor: "text-[#F1935C]",
          iconBgColor: "bg-orange-100",
          items: createItems(section.simulation, false),
        },
        {
          title: "Optimal",
          titleColor: "text-[#34656D]",
          iconColor: "text-[#34656D]",
          iconBgColor: "bg-[#E2F6F6]",
          items: createItems(section.optimal, false),
        },
      ];

      setCostData(formattedData);
    }
  }, [data, refreshKey]);

  const icons = [BaseVector, SimulationVector, OptimalVector];

  return (
    <>
      {loading ? (
        <div className="w-full overflow-auto h-[calc(100vh-410px)]">
          <div className="w-full pb-2">
            <div className="flex lg:flex-nowrap md:flex-wrap justify-around gap-6 w-full">
              <SkeletonLoader />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full overflow-auto h-[calc(100vh-410px)]">
          <div className="w-full pb-2">
            <div className="flex lg:flex-nowrap md:flex-wrap justify-around gap-6 w-full">
              {costData.length > 0 ? (
                <div className="flex lg:flex-nowrap md:flex-wrap gap-4 w-full">
                  {costData.map((section, sectionIndex) => {
                    const SectionIcon = icons[sectionIndex];
                    return (
                      <div
                        key={sectionIndex}
                        className="bg-white border border-[#DFEAF2] rounded-2xl p-2 w-full"
                      >
                        <div className="space-y-3">
                          {section.items.map((item, itemIndex) => (
                            <div
                              key={itemIndex}
                              className="flex items-start gap-3 p-2 mb-2 bg-white border-b border-[#DFEAF2]"
                            >
                              <div
                                className={`${section.iconBgColor} rounded-full p-2 flex-shrink-0`}
                              >
                                <SectionIcon
                                  className={`w-4 h-4 ${section.iconColor}`}
                                />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className="text-xs text-[#819ACC] font-normal">
                                    {item.label}
                                  </span>
                                  {/* {sectionIndex !== 0 && (
                                    <Info className="w-5 h-5 flex-shrink-0" />
                                  )} */}
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-base font-medium text-[#525062]">
                                    {item.value}
                                  </span>
                                  {item.hasPercentage && item.percentage && (
                                    <span
                                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                                        item.isPositive
                                          ? "text-[#1B971F] bg-[#EEFFEC]"
                                          : "text-[#97231B] bg-[#F9EDEB]"
                                      }`}
                                    >
                                      {item.percentage}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8 w-full">
                  No Data Available...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
